import type { ExportNetworkAssetSummary } from './export-network-assets.js';
import {
  EXPORT_COLUMN_GROUPS,
  MANDATORY_EXPORT_COLUMN_IDS,
  NETWORK_EXPORT_COLUMNS,
} from './export-network-columns.js';
import type { ExportNetworkDataPointGroup } from './export-network-data-points.js';

/** Which selection a leaf belongs to — each lives in its own state. */
export type ExportLeafSource =
  | 'column'
  | 'dataPoint'
  | 'certification'
  | 'audit'
  | 'document';

/** A selectable leaf: one company column, or one data point. */
export interface ExportTreeLeaf {
  kind: 'leaf';
  id: string;
  source: ExportLeafSource;
  /** Resolved label. Data points fall back to their id when the lib has none. */
  name: string;
  /** How many exported companies answered or hold it — absent for columns. */
  answeredCompaniesCount?: number;
  /** The backend appends these whatever the payload says, so they cannot be unticked. */
  alwaysIncluded?: true;
}

/** A branch: a section, an impact area, a template, or a column group. */
export interface ExportTreeBranch {
  kind: 'branch';
  id: string;
  name: string;
  children: ExportTreeNode[];
}

export type ExportTreeNode = ExportTreeBranch | ExportTreeLeaf;

/** Tri-state for a branch's checkbox. */
export type ExportTreeSelection = 'all' | 'some' | 'none';

export interface ExportTreeCounts {
  selected: number;
  total: number;
}

/**
 * What the tree reads from a catalogue group.
 *
 * Deliberately narrower than the wire type: the rail nests by template, not by
 * section, so requiring `sectionPath` here would force every caller — and every
 * test fixture — to carry a field the builder never looks at.
 */
export type ExportTreeDataPointGroup = Pick<
  ExportNetworkDataPointGroup,
  'templateId' | 'templateName' | 'dataPoints'
>;

/** Root id of the company-columns branch, so callers can address it. */
export const COMPANY_COLUMNS_NODE_ID = 'company-columns';

/** Root id of the data points branch when the catalogue carries no sections. */
export const DATA_POINTS_NODE_ID = 'data-points';

export const CERTIFICATIONS_NODE_ID = 'certifications';

export const AUDITS_NODE_ID = 'audits';

export const DOCUMENTS_NODE_ID = 'documents';

/** Standards with no type, and audit types with no category. */
export const OTHER_ASSET_GROUP_ID = 'other';

export interface BuildExportTreeParams {
  /** Resolves a column id to its label. */
  columnLabel: (id: string) => string;
  /** Resolves a column group id to its label. */
  columnGroupLabel: (group: string) => string;
  /** Label for the company-columns root. */
  companyColumnsLabel: string;
  /** Label for the data points root when the catalogue has no sections. */
  dataPointsLabel: string;
  /** Data point groups in scope, already filtered by answered-ness. */
  dataPointGroups: ExportTreeDataPointGroup[];
  /**
   * The asset branches are optional so a caller that predates them — or one
   * whose catalogue reports no permission — keeps getting the two-branch tree.
   * An empty list hides its branch entirely; never render an empty pane.
   */
  certifications?: ExportNetworkAssetSummary[];
  certificationsLabel?: string;
  /** Resolves a standard type to its label; `OTHER_ASSET_GROUP_ID` for null. */
  certificationGroupLabel?: (group: string) => string;
  audits?: ExportNetworkAssetSummary[];
  auditsLabel?: string;
  documents?: ExportNetworkAssetSummary[];
  documentsLabel?: string;
}

const columnLeaves = (
  group: string,
  columnLabel: (id: string) => string,
): ExportTreeLeaf[] =>
  NETWORK_EXPORT_COLUMNS.filter(column => column.group === group).map(
    column => ({
      kind: 'leaf' as const,
      source: 'column' as const,
      id: column.id,
      name: columnLabel(column.id),
      ...(MANDATORY_EXPORT_COLUMN_IDS.includes(column.id)
        ? { alwaysIncluded: true as const }
        : {}),
    }),
  );

const assetLeaf = (
  item: ExportNetworkAssetSummary,
  source: 'certification' | 'audit' | 'document',
): ExportTreeLeaf => ({
  kind: 'leaf',
  source,
  id: item.id,
  name: item.name ?? item.id,
  answeredCompaniesCount: item.companiesCount,
});

/**
 * Certifications nest one level by standard type; there are enough of them that
 * a flat list is unreadable. Audit types stay flat — far fewer, and their
 * category is not how anyone looks for them.
 */
const certificationBranches = (
  items: ExportNetworkAssetSummary[],
  groupLabel: (group: string) => string,
): ExportTreeNode[] => {
  const byGroup = new Map<string, ExportNetworkAssetSummary[]>();
  for (const item of items) {
    const group = item.group ?? OTHER_ASSET_GROUP_ID;
    byGroup.set(group, [...(byGroup.get(group) ?? []), item]);
  }

  return [...byGroup.entries()].map(([group, groupItems]) => ({
    kind: 'branch' as const,
    id: `${CERTIFICATIONS_NODE_ID}:${group}`,
    name: groupLabel(group),
    children: groupItems.map(item => assetLeaf(item, 'certification')),
  }));
};

/**
 * The Custom picker's tree.
 *
 * Company columns sit alongside the data points as a top-level branch so the
 * rail has one navigation model rather than two.
 *
 * Section support is deliberate but dormant: the catalogue response carries no
 * section yet, so every template hangs off one `Data points` root. When the
 * backend adds it, the templates nest under `GENERAL INFORMATION` / `ESG`
 * without touching the components — grouping happens here.
 */
export function buildExportTree({
  columnLabel,
  columnGroupLabel,
  companyColumnsLabel,
  dataPointsLabel,
  dataPointGroups,
  certifications = [],
  certificationsLabel = '',
  certificationGroupLabel = group => group,
  audits = [],
  auditsLabel = '',
  documents = [],
  documentsLabel = '',
}: BuildExportTreeParams): ExportTreeBranch[] {
  const companyColumns: ExportTreeBranch = {
    kind: 'branch',
    id: COMPANY_COLUMNS_NODE_ID,
    name: companyColumnsLabel,
    children: EXPORT_COLUMN_GROUPS.map(group => ({
      kind: 'branch' as const,
      id: `${COMPANY_COLUMNS_NODE_ID}:${group}`,
      name: columnGroupLabel(group),
      children: columnLeaves(group, columnLabel),
    })),
  };

  const assetBranches: ExportTreeBranch[] = [];
  if (certifications.length > 0) {
    assetBranches.push({
      kind: 'branch',
      id: CERTIFICATIONS_NODE_ID,
      name: certificationsLabel,
      children: certificationBranches(certifications, certificationGroupLabel),
    });
  }
  if (audits.length > 0) {
    assetBranches.push({
      kind: 'branch',
      id: AUDITS_NODE_ID,
      name: auditsLabel,
      children: audits.map(item => assetLeaf(item, 'audit')),
    });
  }
  if (documents.length > 0) {
    assetBranches.push({
      kind: 'branch',
      id: DOCUMENTS_NODE_ID,
      name: documentsLabel,
      children: documents.map(item => assetLeaf(item, 'document')),
    });
  }

  if (dataPointGroups.length === 0) return [companyColumns, ...assetBranches];

  const dataPoints: ExportTreeBranch = {
    kind: 'branch',
    id: DATA_POINTS_NODE_ID,
    name: dataPointsLabel,
    children: dataPointGroups.map(group => ({
      kind: 'branch' as const,
      id: group.templateId,
      name: group.templateName ?? group.templateId,
      children: group.dataPoints.map(dataPoint => ({
        kind: 'leaf' as const,
        source: 'dataPoint' as const,
        id: dataPoint.id,
        name: dataPoint.name ?? dataPoint.id,
        answeredCompaniesCount: dataPoint.answeredCompaniesCount,
      })),
    })),
  };

  return [companyColumns, dataPoints, ...assetBranches];
}

/**
 * The leaf ids under a node, partitioned by which selection owns them.
 *
 * Lives here rather than in each app's hook: a branch toggle has to split its
 * leaves across four separate pieces of state, and that partition is the part
 * two hand-written copies would get subtly different.
 */
export function leafIdsBySource(
  node: ExportTreeNode,
): Record<ExportLeafSource, string[]> {
  const ids: Record<ExportLeafSource, string[]> = {
    column: [],
    dataPoint: [],
    certification: [],
    audit: [],
    document: [],
  };

  for (const leaf of collectLeaves(node)) {
    ids[leaf.source].push(leaf.id);
  }

  return ids;
}

/** Every leaf beneath a node, in render order. */
export function collectLeaves(node: ExportTreeNode): ExportTreeLeaf[] {
  if (node.kind === 'leaf') return [node];
  return node.children.flatMap(collectLeaves);
}

/**
 * Counts under a node.
 *
 * `alwaysIncluded` leaves count as selected, because they ship regardless —
 * showing `22/29` while the file has 29 is the mismatch this whole dialog has
 * already been bitten by once.
 */
export function nodeCounts(
  node: ExportTreeNode,
  isSelected: (leaf: ExportTreeLeaf) => boolean,
): ExportTreeCounts {
  const leaves = collectLeaves(node);
  return {
    selected: leaves.filter(leaf => leaf.alwaysIncluded || isSelected(leaf))
      .length,
    total: leaves.length,
  };
}

export function nodeSelection(
  node: ExportTreeNode,
  isSelected: (leaf: ExportTreeLeaf) => boolean,
): ExportTreeSelection {
  const { selected, total } = nodeCounts(node, isSelected);
  if (selected === 0) return 'none';
  return selected === total ? 'all' : 'some';
}

/**
 * Filters the tree to nodes matching a search term.
 *
 * A branch is kept when its own name matches — with all its children, so
 * searching a template name gives you the whole template — or when any
 * descendant leaf matches.
 */
export function filterExportTree(
  nodes: ExportTreeNode[],
  search: string,
): ExportTreeNode[] {
  const term = search.trim().toLowerCase();
  if (term === '') return nodes;

  const visit = (node: ExportTreeNode): ExportTreeNode | null => {
    const matches = node.name.toLowerCase().includes(term);
    if (node.kind === 'leaf') return matches ? node : null;
    if (matches) return node;

    const children = node.children
      .map(visit)
      .filter((child): child is ExportTreeNode => child !== null);
    return children.length > 0 ? { ...node, children } : null;
  };

  return nodes
    .map(visit)
    .filter((node): node is ExportTreeNode => node !== null);
}

/** Finds a node by id, for the detail pane. */
export function findExportTreeNode(
  nodes: ExportTreeNode[],
  id: string,
): ExportTreeNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.kind === 'branch') {
      const found = findExportTreeNode(node.children, id);
      if (found) return found;
    }
  }
  return null;
}
